import React from 'react'
import { cn } from '../../lib/utils'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'

export interface ComboBoxOption<T = string> {
  value: T
  label: string
  keywords?: string[]
  data?: any
}

export interface ComboBoxProps<T = string> {
  value: T
  onChange: (value: T) => void
  options: Array<ComboBoxOption<T>>
  placeholder?: string
  label?: string
  buttonClassName?: string
  listClassName?: string
  inputPlaceholder?: string
  renderButtonContent?: (selected?: ComboBoxOption<T>) => React.ReactNode
  renderOption?: (opt: ComboBoxOption<T>, selected: boolean) => React.ReactNode
  onOpenChange?: (open: boolean) => void
}

export function ComboBox<T = string>({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  inputPlaceholder = 'Search...',
  renderButtonContent,
  renderOption,
  buttonClassName,
  listClassName,
  onOpenChange,
}: ComboBoxProps<T>) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const listboxId = React.useId()
  // Track the active (highlighted) item for keyboard navigation
  const listRef = React.useRef<HTMLUListElement | null>(null)
  const [activeIndex, setActiveIndex] = React.useState<number>(-1)
  const optionId = React.useCallback((idx: number) => `${listboxId}-option-${idx}`, [listboxId])
  const hasModifier = (e: React.KeyboardEvent) => e.altKey || e.ctrlKey || e.metaKey || e.shiftKey

  // Keep mounted to allow exit animation
  const [mounted, setMounted] = React.useState(false)
  const ANIMATION_MS = 200;

  // Notify consumer on open state change
  React.useEffect(() => {
    onOpenChange?.(open)
  }, [open, onOpenChange])

  // Portal + positioning state (fixed width, wider than trigger)
  const portalRef = React.useRef<HTMLDivElement | null>(null)
  const DROPDOWN_WIDTH = 560
  const [position, setPosition] = React.useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: DROPDOWN_WIDTH })
  const updatePosition = React.useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const maxWidth = Math.min(DROPDOWN_WIDTH, window.innerWidth - 16) // keep margins
    const clampedLeft = Math.min(Math.max(rect.left, 8), window.innerWidth - maxWidth - 8)
    const top = Math.round(rect.bottom + 4)
    setPosition({ top, left: Math.round(clampedLeft), width: Math.round(maxWidth) })
  }, [])

  const selected = React.useMemo(() => options.find(o => o.value === value), [options, value])

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => {
      // Search in label
      if (o.label.toLowerCase().includes(q)) return true
      // Search in keywords if they exist
      if (o.keywords) {
        return o.keywords.some(keyword => keyword.toLowerCase().includes(q))
      }
      return false
    })
  }, [options, query])

  React.useEffect(() => {
    if (!open) return
    // Autofocus and autofilter when opening (select all for quick typing)
    const t = setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 10)
    return () => clearTimeout(t)
  }, [open])

  // Position portal on open, and keep in sync on scroll/resize
  React.useEffect(() => {
    if (!open) return
    updatePosition()
    const onScroll = () => updatePosition()
    const onResize = () => updatePosition()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [open, updatePosition])

  // Initialize/reset activeIndex when opening or when the filtered options change while open
  React.useEffect(() => {
    if (!open) return
    const selIdx = filtered.findIndex(o => o.value === selected?.value)
    if (selIdx >= 0) {
      setActiveIndex(selIdx)
    } else {
      setActiveIndex(filtered.length > 0 ? 0 : -1)
    }
  }, [open, filtered, selected])

  // Ensure activeIndex stays within bounds and keep the active item in view
  React.useEffect(() => {
    if (!open) return
    if (activeIndex < 0 || activeIndex >= filtered.length) {
      setActiveIndex(filtered.length > 0 ? 0 : -1)
      return
    }
    const el = document.getElementById(optionId(activeIndex))
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, filtered.length, open, optionId])

  // Close on outside click / Escape
  React.useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node
      const inTrigger = !!containerRef.current?.contains(target)
      const inPortal = !!portalRef.current?.contains(target)
      if (!inTrigger && !inPortal) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Mount/unmount with transition
  React.useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    // Reset active index when closing
    setActiveIndex(-1)
    const t = setTimeout(() => setMounted(false), ANIMATION_MS)
    return () => clearTimeout(t)
  }, [open])

  const handleSelect = (val: T) => {
    onChange(val)
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => {
          if (hasModifier(e)) return
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            if (!open) {
              setOpen(true)
            } else {
              setActiveIndex((idx) => {
                const len = filtered.length
                if (!len) return -1
                return e.key === 'ArrowDown' ? (idx + 1 + len) % len : (idx - 1 + len) % len
              })
            }
          } else if (e.key === 'Enter' && open) {
            e.preventDefault()
            if (activeIndex >= 0 && activeIndex < filtered.length) {
              handleSelect(filtered[activeIndex].value)
            }
          }
        }}
        className={cn(
          'w-full h-10 inline-flex items-center justify-between rounded-lg border border-black/10 bg-white px-3 text-sm text-foreground hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          buttonClassName,
        )}
      >
        {renderButtonContent ? (
          renderButtonContent(selected)
        ) : (
          <span className={cn('truncate text-left w-full', !selected && 'text-foreground/60')}>
            {selected ? selected.label : placeholder}
          </span>
        )}
        <ChevronDown
          className={cn(
            "h-4 w-4 text-foreground/60 transition-transform duration-200 ease-in-out",
            open && "rotate-180"
          )}
        />
      </button>

      {mounted && createPortal(
        <div
          ref={portalRef}
          style={{ position: 'fixed', top: position.top, left: position.left, width: position.width }}
          className={cn(
            'z-50 rounded-lg border border-black/10 bg-white shadow-md origin-top will-change-transform will-change-opacity ease-in ease-out transition duration-200',
            open ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
          )}
        >
          <div className="p-2 border-b border-black/5">
            <div className="relative">
              <input
                ref={inputRef}
                role="combobox"
                aria-controls={listboxId}
                aria-expanded={open}
                aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
                placeholder={inputPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (hasModifier(e)) return
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault()
                    setActiveIndex((idx) => {
                      const len = filtered.length
                      if (!len) return -1
                      return e.key === 'ArrowDown' ? (idx + 1 + len) % len : (idx - 1 + len) % len
                    })
                  } else if (e.key === 'Enter') {
                    e.preventDefault()
                    if (activeIndex >= 0 && activeIndex < filtered.length) {
                      handleSelect(filtered[activeIndex].value)
                    }
                  }
                }}
                className={cn('w-full h-9 rounded-md border border-black/10 bg-white text-sm text-foreground placeholder:text-foreground/50 focus:outline-none focus:ring-2 focus:ring-brand-500 px-3')}
              />
            </div>
          </div>
          <ul id={listboxId} role="listbox" ref={listRef} className={cn('max-h-56 overflow-auto p-1', listClassName)}>
            {filtered.map((opt, idx) => (
              <li id={optionId(idx)} key={String(opt.value)} role="option" aria-selected={selected?.value === opt.value}>
                <div
                  role="button"
                  tabIndex={-1}
                  onClick={() => handleSelect(opt.value)}
                  className={cn('w-full flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-black/5', selected?.value === opt.value && 'bg-brand-50 text-brand-700', activeIndex === idx && 'bg-black/5')}
                >
                  {renderOption ? (
                    <div className="flex w-full items-center justify-between gap-2">
                      {renderOption(opt, selected?.value === opt.value)}
                      {selected?.value === opt.value && (
                        <svg className="h-4 w-4 text-brand-700" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M16.704 5.29a1 1 0 00-1.408-1.42L7.5 11.666 4.704 8.87a1 1 0 00-1.408 1.42l3.5 3.5a1 1 0 001.408 0l8.5-8.5z" clipRule="evenodd" /></svg>
                      )}
                    </div>
                  ) : (
                    <>
                      <span>{opt.label}</span>
                      {selected?.value === opt.value && (
                        <svg className="h-4 w-4 text-brand-700" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M16.704 5.29a1 1 0 00-1.408-1.42L7.5 11.666 4.704 8.87a1 1 0 00-1.408 1.42l3.5 3.5a1 1 0 001.408 0l8.5-8.5z" clipRule="evenodd" /></svg>
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-2.5 py-2 text-sm text-foreground/60">No options</li>
            )}
          </ul>
        </div>,
        document.body
      )}
    </div>
  )
}

export default ComboBox
